import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Corti, CortiClient, CortiEnvironment } from '@corti/sdk';
import { useQueryClient } from '@tanstack/react-query';
import { fetchCortiToken } from 'hooks/cortiAuth';

type TranscribeSocket = Awaited<ReturnType<CortiClient['transcribe']['connect']>>;
export type Messages =
  | Corti.TranscribeConfigStatusMessage
  | Corti.TranscribeUsageMessage
  | Corti.TranscribeDeltaUsageMessage
  | Corti.TranscribeEndedMessage
  | Corti.TranscribeErrorMessage
  | Corti.TranscribeTranscriptMessage
  | Corti.TranscribeCommandMessage
  | Corti.TranscribeFlushedMessage
  | Corti.TranscribeAudioEventMessage
  | Corti.TranscribeCommandsUpdateAcceptedMessage
  | Corti.TranscribeCommandsUpdateDeniedMessage;

interface CortiContextType {
  connect: (configuration?: Partial<Corti.TranscribeConfig>) => Promise<void>;
  sendAudio: (chunk: ArrayBufferLike | Blob | ArrayBufferView) => void;
  sendEnd: () => void;
  handleMessage: (cb: (message: Messages) => void) => void;
  close: () => void;
}

const CortiContext = createContext<CortiContextType | undefined>(undefined);

export const CortiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cortiClient, setCortiClient] = useState<CortiClient | null>(null);
  const queryClient = useQueryClient();
  const wsRef = useRef<TranscribeSocket | null>(null);
  const messageHandlerRef = useRef<((message: Messages) => void) | null>(null);

  useEffect(() => {
    if (cortiClient) return;

    const client = new CortiClient({
      tenantName: 'base',
      environment: CortiEnvironment.Eu,
      auth: {
        refreshAccessToken: async () => {
          return await fetchCortiToken(queryClient);
        },
      },
    });

    setCortiClient(client);
  }, [queryClient, cortiClient]);

  const connect = useCallback(
    async (configuration?: Partial<Corti.TranscribeConfig>) => {
      if (!cortiClient) {
        console.error('Corti client not initialized');
        return;
      }

      if (wsRef.current) {
        // Already connected or connecting; close and reconnect
        try {
          wsRef.current.close();
        } catch (error) {
          console.error(error);
        }
        wsRef.current = null;
      }

      try {
        const ws = await cortiClient.transcribe.connect({
          configuration: {
            primaryLanguage: 'de',
            interimResults: true,
            automaticPunctuation: true,
            formatting: { dates: 'locale:short', numbers: 'numerals', ordinals: 'numerals' },
            ...configuration,
          },
        });
        // In @corti/sdk v2, connect() resolves only after the socket has opened
        // and CONFIG_ACCEPTED has been received, so the socket is ready to use.
        wsRef.current = ws;
        ws.on('message', (message) => {
          messageHandlerRef.current?.(message);
        });
        ws.on('error', (err) => {
          console.error(err);
        });
        ws.on('close', () => {
          wsRef.current = null;
        });
      } catch (e) {
        console.error(e);
      }
    },
    [cortiClient]
  );

  const sendAudio = useCallback((chunk: ArrayBufferLike | Blob | ArrayBufferView) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current?.sendAudio(chunk);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const sendEnd = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        const message = {
          type: 'end' as const,
        };
        wsRef.current?.sendEnd(message);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const handleMessage = useCallback((cb: (message: Messages) => void) => {
    messageHandlerRef.current = cb;
  }, []);

  const close = useCallback(() => {
    try {
      wsRef.current?.close();
    } catch (error) {
      console.error(error);
    }
    wsRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      try {
        wsRef.current?.close();
      } catch (error) {
        console.error(error);
      }
      wsRef.current = null;
    };
  }, []);

  const value: CortiContextType = {
    connect,
    sendAudio,
    sendEnd,
    handleMessage,
    close,
  };

  return <CortiContext.Provider value={value}>{children}</CortiContext.Provider>;
};

export const useCorti = (): CortiContextType => {
  const ctx = useContext(CortiContext);
  if (!ctx) {
    throw new Error('useCorti must be used within a CortiProvider');
  }
  return ctx;
};
